import React, { useCallback, useEffect, useRef, useState } from "react";
import { getNativeSafeAsset } from "../lib/platformAssets";

export function compareVersions(a, b) {
  const pa = String(a || "").split(".").map(Number);
  const pb = String(b || "").split(".").map(Number);
  for (let i = 0; i < 3; i += 1) {
    if ((pa[i] || 0) > (pb[i] || 0)) return 1;
    if ((pa[i] || 0) < (pb[i] || 0)) return -1;
  }
  return 0;
}

export function useVersionCheck(sessionActive = false, deps = {}) {
  const {
    isOnline = false,
    CLIENT_VERSION = "0.0.0",
    fetchServerVersion,
    requestWarmScope,
    allowTechnicalReloadWindow,
    performSoftReloadToLatest,
    waitForNextController,
    waitForServiceWorkerActivationAck,
    buildUpdateUrl,
    windowObj = typeof window !== "undefined" ? window : null,
  } = deps || {};

  const [state, setState] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [serverVersion, setServerVersion] = useState(null);
  const stateRef = useRef(state);
  const lastVersionFocusCheckRef = useRef(0);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  function isDismissed(version) {
    try {
      const dismissedVersion = sessionStorage.getItem("fz:update:dismissed") || "";
      const postponeUntil = Number(sessionStorage.getItem("fz:update:postponeUntil") || 0);
      if (dismissedVersion !== String(version || "")) return false;
      if (!postponeUntil) return true;
      return Date.now() < postponeUntil;
    } catch (e) {
      return false;
    }
  }

  const requestWarmCache = useCallback(async () => {
    await requestWarmScope?.("boot", "update-check");
  }, [requestWarmScope]);

  function allowTechnicalReload(windowMs = 12000) {
    allowTechnicalReloadWindow?.(CLIENT_VERSION, windowMs);
  }

  async function softReloadToLatest(options = {}) {
    const skipPrepare = !!options.skipPrepare;
    const targetVersion = serverVersion || CLIENT_VERSION;
    await performSoftReloadToLatest?.({
      targetVersion,
      clientVersion: CLIENT_VERSION,
      skipPrepare,
      prepareRefresh: windowObj?.__fzPrepareTechnicalRefresh,
      allowReloadWindow: allowTechnicalReloadWindow,
    });
  }

  async function startUpdate() {
    setState("updating");
    setProgress(0);
    let p = 0;
    const interval = setInterval(() => {
      p += Math.random() * 8 + 4;
      if (p >= 85) {
        p = 85;
        clearInterval(interval);
      }
      setProgress(Math.min(p, 85));
    }, 220);

    const expectedVersion = String(serverVersion || CLIENT_VERSION || "");

    try {
      if (typeof windowObj?.__fzPrepareTechnicalRefresh === "function") {
        await windowObj.__fzPrepareTechnicalRefresh(expectedVersion || CLIENT_VERSION);
      }
    } catch (e) {}

    try {
      await requestWarmCache();
    } catch (e) {}

    await waitForNextController?.(8000);
    await waitForServiceWorkerActivationAck?.(expectedVersion, 2500);

    clearInterval(interval);
    setProgress(100);
    await new Promise((resolve) => setTimeout(resolve, 400));
    allowTechnicalReload(18000);
    try {
      const cleanUrl = buildUpdateUrl?.(expectedVersion || CLIENT_VERSION, windowObj?.location?.href);
      if (cleanUrl && windowObj?.location?.replace) {
        windowObj.location.replace(cleanUrl);
      } else {
        throw new Error("reload-url-unavailable");
      }
    } catch (e) {
      softReloadToLatest({ skipPrepare: true }).catch(() => {
        try { windowObj?.location?.reload?.(); } catch (_) {}
      });
    }
  }

  const checkForUpdates = useCallback(async (options = {}) => {
    const keepVisible = !!options.keepVisible;
    const ignoreDismissed = !!options.ignoreDismissed;
    const forceVisible = !!options.forceVisible;
    const currentState = stateRef.current;
    const nextCheckingState = currentState === "updating" || keepVisible ? currentState : "checking";
    if (currentState !== "updating" && !keepVisible) setState(nextCheckingState);
    try {
      const srvVer = await fetchServerVersion?.();
      if (!srvVer) {
        if (stateRef.current !== "updating" && !keepVisible) setState((prev) => (prev === "checking" ? "idle" : prev));
        return null;
      }
      setServerVersion(String(srvVer));
      try { localStorage.setItem("fz:appVersion:server", String(srvVer)); } catch (e) {}
      if (compareVersions(String(srvVer), CLIENT_VERSION) > 0) {
        requestWarmCache();
        const canShowUpdate = ignoreDismissed || !isDismissed(srvVer) || forceVisible;
        if (canShowUpdate && stateRef.current !== "updating") setState("update_available");
        else if (stateRef.current !== "updating" && !keepVisible) setState("idle");
      } else if (stateRef.current !== "updating") {
        setState("idle");
      }
      return String(srvVer);
    } catch (e) {
      if (stateRef.current !== "updating" && !keepVisible) setState((prev) => (prev === "checking" ? "idle" : prev));
      return null;
    }
  }, [CLIENT_VERSION, fetchServerVersion, requestWarmCache]);

  useEffect(() => {
    if (!isOnline) return undefined;
    let alive = true;
    const run = async (keepVisible = false) => {
      const srvVer = await checkForUpdates({ keepVisible });
      if (!alive || !srvVer) return;
    };
    run(false);
    const timer = setInterval(() => {
      if (!alive) return;
      const currentState = stateRef.current;
      run(currentState === "update_available" || currentState === "updating");
    }, 5 * 60 * 1000);
    const onFocus = () => {
      if (!alive) return;
      const now = Date.now();
      if (now - lastVersionFocusCheckRef.current < 60000) return;
      lastVersionFocusCheckRef.current = now;
      const currentState = stateRef.current;
      run(currentState === "update_available" || currentState === "updating");
    };
    const onVisibility = () => {
      if (document.visibilityState !== "visible" || !alive) return;
      onFocus();
    };
    windowObj?.addEventListener?.("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      clearInterval(timer);
      windowObj?.removeEventListener?.("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [checkForUpdates, isOnline, sessionActive, windowObj]);

  function confirmUpdate() {
    try { sessionStorage.removeItem("fz:update:dismissed"); } catch (e) {}
    try { sessionStorage.removeItem("fz:update:postponeUntil"); } catch (e) {}
    setState("updating");
    startUpdate();
  }

  function postpone(versionOverride, delayMs) {
    const targetVersion = String(versionOverride || serverVersion || "");
    const postponeMs = Math.max(15000, Number(delayMs) || 60000);
    try {
      if (targetVersion) sessionStorage.setItem("fz:update:dismissed", targetVersion);
      sessionStorage.setItem("fz:update:postponeUntil", String(Date.now() + postponeMs));
    } catch (e) {}
    setState("idle");
  }

  return { state, progress, serverVersion, confirmUpdate, postpone, checkForUpdates };
}

export function VersionUpdateOverlay({ vcheck }) {
  const { state, progress, serverVersion, confirmUpdate, postpone } = vcheck;

  if (state === "updating") {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.92)", zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 24 }}>
        <img
          src={getNativeSafeAsset("/icons/shared/blue-heeler-online.webp")}
          alt="Blue Heeler online"
          style={{
            width: 110,
            height: 110,
            objectFit: "contain",
            display: "block",
            filter: "drop-shadow(0 6px 18px rgba(59,130,246,.22))"
          }}
        />
        <div style={{ fontWeight: 800, fontSize: 22, color: "#fff" }}>Atualizando App</div>
        <div style={{ fontSize: 14, color: "#94A3B8" }}>Versão {serverVersion} disponível. Aguarde...</div>
        <div style={{ width: 280, height: 8, background: "#1E293B", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ height: "100%", background: "linear-gradient(90deg,#3B82F6,#8B5CF6)", width: `${progress}%`, transition: "width .3s ease", borderRadius: 8 }} />
        </div>
        <div style={{ fontSize: 12, color: "#64748B" }}>{Math.round(progress)}%</div>
      </div>
    );
  }

  if (state === "update_available") {
    return (
      <div style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", background: "#1E293B", border: "1px solid #3B82F6", borderRadius: 14, padding: "16px 24px", zIndex: 9999, maxWidth: 420, width: "92%", boxShadow: "0 8px 32px rgba(0,0,0,.5)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 20 }}>⚡</span>
          <div style={{ fontWeight: 700, color: "#93C5FD", fontSize: 15 }}>Nova versão disponível ({serverVersion})</div>
        </div>
        <div style={{ fontSize: 13, color: "#94A3B8", marginBottom: 12 }}>
          Há uma atualização disponível. Salve o trabalho antes de atualizar. Você pode adiar por 60 segundos.
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button onClick={() => postpone(serverVersion, 60000)} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #475569", background: "transparent", color: "#94A3B8", cursor: "pointer", fontSize: 13 }}>
            Adiar 60s
          </button>
          <button onClick={confirmUpdate} style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: "#3B82F6", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
            Atualizar
          </button>
        </div>
      </div>
    );
  }

  return null;
}
